import CloseIcon from '@/icons/close';
import DeleteIcon from '@/icons/delete';
import { Dialog, Transition } from '@headlessui/react';
import React, { Fragment, PropsWithChildren, useEffect, useState } from 'react';
import clsx from 'clsx';
import { SavedSearch } from '@/context/property';
import Spinner from '@/icons/spinner';
import { useSavedSearch } from '@/context/property/hooks/use-saved-search';
import { sentryCaptureException } from '@/utility';
import { FORMAT_DATETIME2, formatDate } from '@/utility/date';

interface IconButtonProps {
  onClick?: (event: React.MouseEvent) => void;
  color?: 'primary' | 'danger';
}

function IconButton(props: PropsWithChildren<IconButtonProps>) {
  return (
    <button
      onClick={props.onClick}
      className={clsx(
        'rounded-full p-2 border-2 text-white',
        props.color === 'danger'
          ? 'bg-red-400 border-red-600 hover:bg-red-500'
          : 'bg-primary-400 border-primary-600 hover:bg-primary-500'
      )}
    >
      {props.children}
    </button>
  );
}

interface ListItemProps {
  item: SavedSearch;
  index: number;
  onDeleteSuccess: (item: SavedSearch) => void;
  onApplySuccess: (item: SavedSearch) => void;
}

function ListItem({
  item,
  index,
  onApplySuccess,
  onDeleteSuccess,
}: ListItemProps) {
  const [deleting, setDeleting] = useState(false);
  const { applySearch, deleteSearch } = useSavedSearch();
  const handleSearch = (event: React.MouseEvent, item: SavedSearch) => {
    applySearch(item);
    onApplySuccess(item);
  };

  const handleDelete = async (event: React.MouseEvent, name: string) => {
    event.preventDefault();
    event.stopPropagation();
    setDeleting(true);
    try {
      await deleteSearch(name);
      onDeleteSuccess(item);
    } catch (error) {
      console.error(error);
      setDeleting(false);
      sentryCaptureException(error);
    }
  };

  return (
    <button
      className={clsx(
        'flex flex-row items-center py-2 px-6 w-full hover:bg-gray-100 text-left',
        index > 0 && 'border-t-2'
      )}
      onClick={(event: React.MouseEvent) => handleSearch(event, item)}
    >
      <div className="flex flex-col flex-1 items-start">
        <div className="text-lg font-bold text-gray-600">
          <span>{item.name}</span>
        </div>
        <div className="text-gray-500">
          <span>{formatDate(item.createdOn, FORMAT_DATETIME2)}</span>
        </div>
      </div>
      {!deleting && (
        <div className="flex gap-3">
          <IconButton
            color="danger"
            onClick={(event: React.MouseEvent) =>
              handleDelete(event, item.name)
            }
          >
            <DeleteIcon width={16} height={16} />
          </IconButton>
        </div>
      )}
    </button>
  );
}

export interface ISaveSearchListModalProps {
  open: boolean;
  toggleModal: () => void;
  message?: string;
  booking?: boolean;
}

export const SaveSearchListModal = ({
  open,
  toggleModal,
}: ISaveSearchListModalProps) => {
  const [loading, setLoading] = useState(true);
  const { getAll } = useSavedSearch();
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);

  useEffect(() => {
    if (!open) return;
    getAll().then((res) => {
      setSavedSearches(res);
      setLoading(false);
    });
  }, [open, getAll]);

  const onApplySuccess = () => {
    toggleModal();
  };

  const onDeleteSuccess = (item: SavedSearch) => {
    setSavedSearches(savedSearches.filter((s) => s.name !== item.name));
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        onClose={toggleModal}
      >
        <div className="min-h-screen sm:px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50" />
          </Transition.Child>

          <span
            className="inline-block h-screen align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="inline-block w-full max-w-4xl sm:my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl sm:rounded-2xl ">
              <Dialog.Title
                as="h3"
                className="text-lg font-semibold leading-6 text-gray-900 bg-gray-50 px-6 py-4 flex items-center justify-between"
              >
                <span>Saved Search List</span>
                <button onClick={toggleModal}>
                  <CloseIcon className="h-5 w-5" />
                </button>
              </Dialog.Title>
              <div className="py-5 overflow-auto">
                {loading && (
                  <div className="flex items-center justify-center h-8">
                    <Spinner className="h-6 w-6 text-primary-500 spin" />
                  </div>
                )}
                {!loading && !savedSearches.length && (
                  <h2 className="text-xl text-gray-500 text-center py-5">
                    No saved search
                  </h2>
                )}
                {savedSearches.map((item, index) => (
                  <ListItem
                    key={index + item.name}
                    index={index}
                    item={item}
                    onApplySuccess={onApplySuccess}
                    onDeleteSuccess={onDeleteSuccess}
                  />
                ))}
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};
